import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, ListGroup, Spinner, InputGroup, Alert, Card, Row, Col, Stack, Badge } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { useGlobalState } from '../contexts/GlobalContext';
import { apiFetch } from '../utils/api';
import { createStripeCheckoutSession, createStripePortalSession, getStripeCheckoutSessionStatus, getStripeSubscriptionStatus } from '../utils/stripeApi';
import { isRealtor } from '../utils/userAccess';

const STRIPE_CUSTOMER_PORTAL_URL = 'https://billing.stripe.com/p/login/7sYbJ18Js7Ng9me4k6dUY00';

const CONTACT_EXPORT_COLUMNS = [
  ['Contact ID', (contact) => contact.id],
  ['Name', (contact) => contact.name],
  ['Email', (contact) => contact.email],
  ['Phone', (contact) => contact.phone],
  ['Role', (contact) => contact.role],
  ['Company', (contact) => contact.company_name],
  ['LLC(s)', (contact) => contact.llcs],
  ['Job Title', (contact) => contact.job_title],
  ['Industry', (contact) => contact.industry],
  ['Mailing Street', (contact) => contact.mailAddress],
  ['Mailing City', (contact) => contact.mailCity],
  ['Mailing State', (contact) => contact.mailSt],
  ['Mailing ZIP', (contact) => contact.mailZip],
  ['Priority', (contact) => contact.priority],
  ['Prospect Stage', (contact) => contact.prospect_stage],
  ['Estimated Value', (contact) => contact.estimated_value],
  ['Budget', (contact) => contact.budget],
  ['Next Step', (contact) => contact.next_step],
  ['Next Follow-Up', (contact) => contact.next_follow_up_at],
  ['Last Contacted', (contact) => contact.last_contacted_at],
  ['Preferred Contact Method', (contact) => contact.preferred_contact_method],
  ['Lead Source', (contact) => contact.lead_source],
  ['Notes', (contact) => contact.notes],
  ['Linked Properties', (contact) => (contact.properties || []).map((property) => [property.streetAddress, property.city, property.state, property.zipCode].filter(Boolean).join(', ')).filter(Boolean).join('; ')],
  ['Open Tasks', (contact) => (contact.tasks || []).filter((task) => String(task.status || '').toLowerCase() !== 'closed').map((task) => task.taskDescription || task.taskType).filter(Boolean).join('; ')],
  ['Created At', (contact) => contact.createdAt],
];

const escapeCsvCell = (value) => {
  let cell = value === null || value === undefined ? '' : String(value);
  if (/^[=+\-@]/.test(cell)) cell = `'${cell}`;
  return `"${cell.replace(/"/g, '""')}"`;
};

function getBillingParams(location) {
  const searchParams = new URLSearchParams(location.search);
  const hashParams = new URLSearchParams((location.hash || '').replace(/^#/, ''));

  return {
    billingState: searchParams.get('billing') || hashParams.get('billing'),
    sessionId: searchParams.get('session_id') || hashParams.get('session_id'),
  };
}

function Account() {
  const { state, dispatch } = useGlobalState();
  const location = useLocation();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showUserIdModal, setShowUserIdModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showJoinTeamModal, setShowJoinTeamModal] = useState(false);
  const [showBotKeyModal, setShowBotKeyModal] = useState(false);

  const [passwords, setPasswords] = useState({ newPassword: '', confirmPassword: '' });
  const [passwordMessage, setPasswordMessage] = useState('');
  const [showPasswordFields, setShowPasswordFields] = useState({ new: false, confirm: false });
  const [accountSuccess, setAccountSuccess] = useState('');

  const [newUserId, setNewUserId] = useState(state.user?.userId || state.user?.user_id || '');
  const [userIdMessage, setUserIdMessage] = useState('');

  const [query, setQuery] = useState('');
  const [dbResults, setDbResults] = useState([]);
  const [pendingDb, setPendingDb] = useState(false);
  const [teamError, setTeamError] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [joinQuery, setJoinQuery] = useState('');
  const [teamSearchResults, setTeamSearchResults] = useState([]);
  const [pendingTeamSearch, setPendingTeamSearch] = useState(false);
  const [selectedJoinTeam, setSelectedJoinTeam] = useState(null);
  const [joinMessage, setJoinMessage] = useState('');
  const [joinRequestError, setJoinRequestError] = useState('');
  const [submittingJoinRequest, setSubmittingJoinRequest] = useState(false);
  const [botKeys, setBotKeys] = useState([]);
  const [loadingBotKeys, setLoadingBotKeys] = useState(false);
  const [botKeyError, setBotKeyError] = useState('');
  const [creatingBotKey, setCreatingBotKey] = useState(false);
  const [revokingBotKeyId, setRevokingBotKeyId] = useState(null);
  const [newBotKey, setNewBotKey] = useState({
    label: '',
    expires_at: '',
  });
  const [createdBotToken, setCreatedBotToken] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState('');
  const [billingBusy, setBillingBusy] = useState(false);
  const [exportingContacts, setExportingContacts] = useState(false);
  const [contactsExportError, setContactsExportError] = useState('');
  const realtorView = isRealtor(state.user);
  const orgLabel = 'Business / Team';
  const currentTeam = state.user?.team || null;

  const buildSelectedTeam = () => {
    const existing = state.user?.team;
    if (existing && typeof existing === 'object') {
      return {
        id: existing.id || null,
        public_id: existing.public_id || existing.publicId || '',
        name: existing.name || '',
        team_type: existing.team_type || existing.teamType || (realtorView ? 'brokerage' : 'business'),
        email: existing.email || '',
        phone: existing.phone || '',
        website: existing.website || '',
        street_address: existing.street_address || existing.streetAddress || '',
        city: existing.city || '',
        state: existing.state || '',
        postal_code: existing.postal_code || existing.postalCode || '',
        country: existing.country || 'US',
        description: existing.description || '',
      };
    }

    return {
      public_id: '',
      name: '',
      team_type: realtorView ? 'brokerage' : 'business',
      email: '',
      phone: '',
      website: '',
      street_address: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US',
      description: '',
    };
  };

  useEffect(() => {
    setNewUserId(state.user?.userId || state.user?.user_id || '');
  }, [state.user]);

  const loadBotKeys = async () => {
    setLoadingBotKeys(true);
    setBotKeyError('');
    try {
      const res = await apiFetch('listBotAccessTokens');
      setBotKeys(res.tokens || []);
    } catch (e) {
      setBotKeyError(e.message || 'Unable to load bot API keys');
    } finally {
      setLoadingBotKeys(false);
    }
  };

  useEffect(() => {
    loadBotKeys();
  }, []);

  const loadSubscription = useCallback(async () => {
    if (!state.user?.email) {
      setSubscription(null);
      return;
    }
    setLoadingSubscription(true);
    setSubscriptionError('');
    try {
      const res = await getStripeSubscriptionStatus(state.user.email);
      setSubscription(res || null);
    } catch (e) {
      setSubscriptionError(e.message || 'Unable to load billing status');
    } finally {
      setLoadingSubscription(false);
    }
  }, [state.user?.email]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  useEffect(() => {
    const { billingState, sessionId } = getBillingParams(location);
    if (billingState === 'success' && sessionId) {
      getStripeCheckoutSessionStatus(sessionId)
        .then((session) => {
          if (session.status === 'complete' && ['paid', 'unpaid', 'no_payment_required'].includes(session.payment_status)) {
            setAccountSuccess('Subscription checkout completed. Billing status has been refreshed.');
            setSubscriptionError('');
            loadSubscription();
            return;
          }

          setSubscriptionError('Stripe checkout did not complete successfully.');
        })
        .catch((e) => {
          setSubscriptionError(e.message || 'Unable to verify Stripe checkout status.');
        });
    } else if (billingState === 'success') {
      setAccountSuccess('Subscription checkout completed. Billing status will update in a few moments.');
      loadSubscription();
    } else if (billingState === 'cancelled') {
      setSubscriptionError('Checkout was cancelled before the subscription was created.');
    }
  }, [location.search, location.hash, loadSubscription]);

  useEffect(() => {
    if (!showJoinTeamModal) return;
    if (!joinQuery || joinQuery.trim().length < 2) {
      setTeamSearchResults([]);
      return;
    }

    const t = setTimeout(async () => {
      setPendingTeamSearch(true);
      try {
        const res = await apiFetch('searchTeams', {
          queryParams: { q: joinQuery.trim() }
        });
        setTeamSearchResults(res.teams || []);
      } catch (e) {
        setJoinRequestError(e.message || 'Unable to search teams');
      } finally {
        setPendingTeamSearch(false);
      }
    }, 300);

    return () => clearTimeout(t);
  }, [joinQuery, showJoinTeamModal]);

  const resetPasswordState = (closeModal = true) => {
    if (closeModal) {
      setShowPasswordModal(false);
    }
    setPasswords({ newPassword: '', confirmPassword: '' });
    setShowPasswordFields({ new: false, confirm: false });
    setPasswordMessage('');
  };

  const handlePasswordChange = async () => {
    if (passwords.newPassword !== passwords.confirmPassword) {
      setPasswordMessage('Passwords do not match');
      return;
    }
    try {
      await apiFetch('changePassword', {
        method: 'POST',
        body: JSON.stringify({ newPassword: passwords.newPassword })
      });
      resetPasswordState();
      setAccountSuccess('Password updated successfully');
    } catch (e) {
      setPasswordMessage(e.message);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswordFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  useEffect(() => {
    if (!showTeamModal || !realtorView) return;
    if (!query || query.trim().length < 2) {
      setDbResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setPendingDb(true);
      try {
        const res = await apiFetch('searchBrokerages', {
          queryParams: { q: query.trim() }
        });
        setDbResults(res.brokerages || []);
      } catch (e) {
        setTeamError(e.message);
      } finally {
        setPendingDb(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, showTeamModal, realtorView]);

  const confirmSelectedTeam = async () => {
    if (!selectedTeam) return;
    try {
      const res = await apiFetch('updateTeam', {
        method: 'POST',
        body: JSON.stringify({ team: selectedTeam })
      });
      if (res.success) {
        dispatch({
          type: 'UPDATE_USER_PROFILE',
          payload: {
            id: state.user.id,
            team: {
              id: res.team.id,
              public_id: res.team.public_id,
              name: res.team.name,
              team_type: res.team.team_type,
              email: res.team.email,
              phone: res.team.phone,
              website: res.team.website,
              street_address: res.team.street_address,
              city: res.team.city,
              state: res.team.state,
              postal_code: res.team.postal_code,
              country: res.team.country,
              description: res.team.description,
            },
          }
        });
        setAccountSuccess('Team updated successfully');
        setShowTeamModal(false);
        setQuery('');
        setSelectedTeam(null);
      }
    } catch (e) {
      setTeamError(e.message);
    }
  };

  const handleUserIdChange = async () => {
    const nextId = (newUserId || '').trim();
    if (!nextId) {
      setUserIdMessage('User ID cannot be empty');
      return;
    }
    if (nextId === (state.user?.userId || state.user?.user_id)) {
      setUserIdMessage('User ID is unchanged');
      return;
    }

    try {
      const res = await apiFetch('changeUserId', {
        method: 'POST',
        body: JSON.stringify({ newUserId: nextId })
      });
      if (res.success) {
        const updatedUser = {
          ...state.user,
          userId: res.userId,
          user_id: res.userId,
        };
        dispatch({ type: 'SET_USER', payload: updatedUser });
        if (res.token) {
          localStorage.setItem('token', res.token);
        }
        setAccountSuccess('User ID updated successfully');
        setShowUserIdModal(false);
        setUserIdMessage('');
      }
    } catch (e) {
      setUserIdMessage(e.message || 'Unable to update user ID');
    }
  };

  const resetJoinRequestModal = () => {
    setShowJoinTeamModal(false);
    setJoinQuery('');
    setTeamSearchResults([]);
    setSelectedJoinTeam(null);
    setJoinMessage('');
    setJoinRequestError('');
    setPendingTeamSearch(false);
    setSubmittingJoinRequest(false);
  };

  const resetBotKeyModal = () => {
    setShowBotKeyModal(false);
    setNewBotKey({ label: '', expires_at: '' });
    setCreatedBotToken('');
    setBotKeyError('');
    setCreatingBotKey(false);
  };

  const handleCreateBotKey = async () => {
    const label = newBotKey.label.trim();
    if (!label) {
      setBotKeyError('API key label is required');
      return;
    }

    setCreatingBotKey(true);
    setBotKeyError('');
    try {
      const body = { label };
      if (newBotKey.expires_at) {
        body.expires_at = newBotKey.expires_at.replace('T', ' ') + ':00';
      }
      const res = await apiFetch('createBotAccessToken', {
        method: 'POST',
        body: JSON.stringify(body)
      });
      setCreatedBotToken(res.token || '');
      setAccountSuccess('Bot API key created successfully');
      setNewBotKey({ label: '', expires_at: '' });
      await loadBotKeys();
    } catch (e) {
      setBotKeyError(e.message || 'Unable to create bot API key');
    } finally {
      setCreatingBotKey(false);
    }
  };

  const handleRevokeBotKey = async (tokenId) => {
    setRevokingBotKeyId(tokenId);
    setBotKeyError('');
    try {
      const res = await apiFetch('revokeBotAccessToken', {
        method: 'POST',
        body: JSON.stringify({ token_id: tokenId })
      });
      setAccountSuccess(res.message || 'Bot API key revoked');
      await loadBotKeys();
    } catch (e) {
      setBotKeyError(e.message || 'Unable to revoke bot API key');
    } finally {
      setRevokingBotKeyId(null);
    }
  };

  const handleJoinRequest = async () => {
    if (!selectedJoinTeam?.id) {
      setJoinRequestError('Select a team first');
      return;
    }

    setSubmittingJoinRequest(true);
    setJoinRequestError('');
    try {
      const res = await apiFetch('requestTeamJoin', {
        method: 'POST',
        body: JSON.stringify({
          team_id: selectedJoinTeam.id,
          message: joinMessage.trim(),
        })
      });
      setAccountSuccess(res.message || 'Join request submitted');
      resetJoinRequestModal();
    } catch (e) {
      setJoinRequestError(e.message || 'Unable to submit join request');
      setSubmittingJoinRequest(false);
    }
  };

  const handleStartTrial = async () => {
    setBillingBusy(true);
    setSubscriptionError('');
    try {
      const res = await createStripeCheckoutSession({
        email: state.user?.email,
        userId: state.user?.id,
        publicUserId: state.user?.userId || state.user?.user_id,
        successPath: '/account',
        cancelPath: '/account',
        trialDays: 14,
      });
      if (res.url) {
        window.location.href = res.url;
        return;
      }
      throw new Error('Stripe checkout URL was not returned.');
    } catch (e) {
      setSubscriptionError(e.message || 'Unable to start subscription checkout');
    } finally {
      setBillingBusy(false);
    }
  };

  const handleManageBilling = async () => {
    setBillingBusy(true);
    setSubscriptionError('');
    try {
      const res = await createStripePortalSession({
        email: state.user?.email,
        returnPath: '/account',
      });
      if (res.url) {
        window.location.href = res.url;
        return;
      }
      throw new Error('Stripe billing portal URL was not returned.');
    } catch (e) {
      setSubscriptionError(e.message || 'Unable to open billing portal');
    } finally {
      setBillingBusy(false);
    }
  };

  const handleExportContacts = async () => {
    const userId = state.user?.id || state.user?.userId || state.user?.user_id;
    if (!userId) {
      setContactsExportError('Unable to identify the signed-in user.');
      return;
    }

    setExportingContacts(true);
    setContactsExportError('');
    try {
      const result = await apiFetch('fetchContacts', {
        queryParams: { user_id: userId },
      });
      const contacts = result.contacts || [];
      const rows = [
        CONTACT_EXPORT_COLUMNS.map(([label]) => escapeCsvCell(label)).join(','),
        ...contacts.map((contact) => CONTACT_EXPORT_COLUMNS.map(([, getValue]) => escapeCsvCell(getValue(contact))).join(',')),
      ];
      const blob = new Blob([`\uFEFF${rows.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contacts-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setAccountSuccess(`${contacts.length} contact${contacts.length === 1 ? '' : 's'} exported to CSV.`);
    } catch (e) {
      setContactsExportError(e.message || 'Unable to export contacts.');
    } finally {
      setExportingContacts(false);
    }
  };

  const subscriptionBadgeVariant = (() => {
    switch (subscription?.status) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'info';
      case 'past_due':
      case 'unpaid':
        return 'warning';
      case 'canceled':
        return 'secondary';
      default:
        return 'dark';
    }
  })();

  return (
    <div className="container py-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h2 className="mb-0">Account</h2>
          <div className="text-muted">Manage your login, user ID, and business/team details.</div>
        </Col>
        <Col xs="auto">
          <Badge bg="light" text="dark" className="border">ID: {state.user?.userId || state.user?.user_id || '—'}</Badge>
        </Col>
      </Row>

      {accountSuccess && (
        <Alert variant="success" onClose={() => setAccountSuccess('')} dismissible className="mb-4">
          {accountSuccess}
        </Alert>
      )}

      <Card className="shadow-sm">
        <Card.Body>
          <Stack gap={3}>
            <div>
              <div className="fw-semibold mb-1">Billing</div>
              <div className="text-muted small mb-2">
                One plan to start: 14 days free, then $7 per month.
              </div>
              <Stack gap={3}>
                {subscriptionError && (
                  <Alert variant="danger" className="mb-0">
                    {subscriptionError}
                  </Alert>
                )}

                {loadingSubscription ? (
                  <div className="text-muted small d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" />
                    Loading subscription status...
                  </div>
                ) : (
                  <Card className="border bg-light-subtle">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                        <div>
                          <div className="fw-semibold">
                            {subscription?.planName || 'Starter'}
                          </div>
                          <div className="small text-muted">
                            {subscription?.hasSubscription
                              ? `Status: ${subscription.status}`
                              : 'No active subscription yet'}
                          </div>
                        </div>
                        <Badge bg={subscriptionBadgeVariant}>
                          {subscription?.status || 'inactive'}
                        </Badge>
                      </div>

                      <div className="small text-muted mt-3">
                        {subscription?.status === 'trialing' && subscription?.trialEnd
                          ? `Trial ends ${new Date(subscription.trialEnd).toLocaleDateString()}${Number.isFinite(subscription?.trialDaysRemaining) ? ` • ${subscription.trialDaysRemaining} day(s) left` : ''}`
                          : subscription?.currentPeriodEnd
                            ? `Current billing period ends ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`
                            : 'Your first checkout starts the trial immediately.'}
                      </div>

                      {subscription?.cancelAtPeriodEnd && (
                        <div className="small text-warning mt-2">
                          Cancellation is scheduled for the end of the current billing period.
                        </div>
                      )}

                      <Stack direction="horizontal" gap={2} className="flex-wrap mt-3">
                        {subscription?.hasSubscription && ['trialing', 'active', 'past_due', 'unpaid'].includes(subscription.status) ? (
                          <Button
                            variant="primary"
                            onClick={handleManageBilling}
                            disabled={billingBusy}
                          >
                            {billingBusy ? 'Opening...' : 'Manage Billing'}
                          </Button>
                        ) : (
                          <Button
                            variant="primary"
                            onClick={handleStartTrial}
                            disabled={billingBusy || !subscription?.configured}
                          >
                            {billingBusy
                              ? 'Opening...'
                              : subscription?.trialEligible
                                ? 'Start 14-Day Free Trial'
                                : 'Subscribe for $7/Month'}
                          </Button>
                        )}
                        <Button
                          variant="outline-secondary"
                          onClick={loadSubscription}
                          disabled={loadingSubscription}
                        >
                          Refresh Billing
                        </Button>
                        <Button
                          as="a"
                          href={STRIPE_CUSTOMER_PORTAL_URL}
                          target="_blank"
                          rel="noreferrer"
                          variant="outline-primary"
                        >
                          Open Stripe Portal
                        </Button>
                      </Stack>

                      {!subscription?.configured && (
                        <div className="small text-danger mt-3">
                          {subscription?.configMessage || 'Stripe is not configured on the server yet. Add the Stripe keys and price ID before using checkout.'}
                        </div>
                      )}

                      {subscription?.configured && subscription?.configMessage && (
                        <div className="small text-warning mt-3">
                          {subscription.configMessage}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                )}
              </Stack>
            </div>

            <hr className="my-2" />

            <div>
              <div className="fw-semibold mb-1">Security</div>
              <div className="text-muted small mb-2">Update credentials tied to your login.</div>
              <Stack direction="horizontal" gap={2}>
                <Button variant="primary" onClick={() => setShowPasswordModal(true)}>
                  Change Password
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    setUserIdMessage('');
                    setNewUserId(state.user?.userId || state.user?.user_id || '');
                    setShowUserIdModal(true);
                  }}
                >
                  Change User ID
                </Button>
              </Stack>
            </div>

            <hr className="my-2" />

            <div>
              <div className="fw-semibold mb-1">Export Contacts</div>
              <div className="text-muted small mb-2">
                Download your contacts, pipeline details, linked properties, and open tasks as a CSV file.
              </div>
              <Button variant="outline-success" onClick={handleExportContacts} disabled={exportingContacts}>
                {exportingContacts ? 'Preparing CSV...' : 'Download Contacts CSV'}
              </Button>
              {contactsExportError && <Alert variant="danger" className="mb-0 mt-3">{contactsExportError}</Alert>}
            </div>

            <hr className="my-2" />

            <div>
              <div className="fw-semibold mb-1">Bot API Keys</div>
              <div className="text-muted small mb-2">
                Create dedicated API keys for AI bots. Each key acts as your SIGNL user and should be stored as a secret.
              </div>
              <Stack gap={3}>
                <Stack direction="horizontal" gap={2} className="flex-wrap">
                  <Button
                    variant="outline-dark"
                    onClick={() => {
                      setBotKeyError('');
                      setCreatedBotToken('');
                      setShowBotKeyModal(true);
                    }}
                  >
                    Create API Key
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={loadBotKeys}
                    disabled={loadingBotKeys}
                  >
                    {loadingBotKeys ? 'Refreshing...' : 'Refresh Keys'}
                  </Button>
                  <Button
                    as="a"
                    href="/ai-bot-instructions.txt"
                    download="ai-bot-instructions.txt"
                    variant="outline-success"
                  >
                    AI Bot Instructions
                  </Button>
                </Stack>

                {botKeyError && (
                  <Alert variant="danger" className="mb-0">
                    {botKeyError}
                  </Alert>
                )}

                {loadingBotKeys ? (
                  <div className="text-muted small d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" />
                    Loading bot API keys...
                  </div>
                ) : botKeys.length > 0 ? (
                  <ListGroup>
                    {botKeys.map((key) => (
                      <ListGroup.Item key={key.id}>
                        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                          <div>
                            <div className="fw-semibold">{key.label}</div>
                            <div className="small text-muted">
                              Created {key.created_at || 'unknown'}
                            </div>
                            <div className="small text-muted">
                              Last used {key.last_used_at || 'never'}{key.expires_at ? ` • Expires ${key.expires_at}` : ' • No expiration'}
                            </div>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <Badge bg={key.is_active ? 'success' : 'secondary'}>
                              {key.is_active ? 'Active' : 'Revoked'}
                            </Badge>
                            {key.is_active && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                disabled={revokingBotKeyId === key.id}
                                onClick={() => handleRevokeBotKey(key.id)}
                              >
                                {revokingBotKeyId === key.id ? 'Revoking...' : 'Revoke'}
                              </Button>
                            )}
                          </div>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <div className="text-muted small">
                    No bot API keys created yet.
                  </div>
                )}
              </Stack>
            </div>

            <hr className="my-2" />

            <div>
              <div className="fw-semibold mb-1">{orgLabel}</div>
              <div className="text-muted small mb-2">
                {realtorView
                  ? 'Create or update the team your office uses.'
                  : 'Create or update the business your team belongs to.'}
              </div>
              {currentTeam ? (
                <Card className="border bg-light-subtle">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                      <div>
                        <div className="fw-semibold">{currentTeam.name}</div>
                        <div className="text-muted small text-capitalize">
                          {currentTeam.teamType || currentTeam.team_type || 'team'}
                        </div>
                      </div>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          setSelectedTeam(buildSelectedTeam());
                          setTeamError('');
                          setQuery('');
                          setShowTeamModal(true);
                        }}
                      >
                        Edit
                      </Button>
                    </div>

                    <div className="small text-muted mt-3">
                      {[currentTeam.streetAddress || currentTeam.street_address, currentTeam.city, currentTeam.state, currentTeam.postalCode || currentTeam.postal_code]
                        .filter(Boolean)
                        .join(', ') || 'No address added yet.'}
                    </div>

                    <div className="small text-muted mt-2">
                      {currentTeam.email || 'No team email'}
                      {(currentTeam.email && (currentTeam.phone || currentTeam.phoneNumber)) ? ' • ' : ''}
                      {currentTeam.phone || currentTeam.phoneNumber || ''}
                    </div>

                    {currentTeam.website && (
                      <div className="small mt-2">
                        <a href={currentTeam.website} target="_blank" rel="noopener noreferrer">
                          {currentTeam.website}
                        </a>
                      </div>
                    )}

                    {currentTeam.description && (
                      <div className="small text-muted mt-2">{currentTeam.description}</div>
                    )}
                  </Card.Body>
                </Card>
              ) : (
                <Stack direction="horizontal" gap={2} className="flex-wrap">
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setSelectedTeam(buildSelectedTeam());
                      setTeamError('');
                      setQuery('');
                      setShowTeamModal(true);
                    }}
                  >
                    {realtorView ? 'Create Team' : 'Create Business'}
                  </Button>
                  <Button
                    variant="outline-primary"
                    onClick={() => {
                      setJoinRequestError('');
                      setShowJoinTeamModal(true);
                    }}
                  >
                    Request to Join
                  </Button>
                </Stack>
              )}
            </div>
          </Stack>
        </Card.Body>
      </Card>

      {/* Password Modal */}
      <Modal
        show={showPasswordModal}
        onHide={() => {
          resetPasswordState();
          setAccountSuccess('');
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Change Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>New Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showPasswordFields.new ? 'text' : 'password'}
                value={passwords.newPassword}
                onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
              />
              <Button
                variant="outline-secondary"
                onClick={() => togglePasswordVisibility('new')}
              >
                {showPasswordFields.new ? 'Hide' : 'Show'}
              </Button>
            </InputGroup>
          </Form.Group>
          <Form.Group>
            <Form.Label>Confirm Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showPasswordFields.confirm ? 'text' : 'password'}
                value={passwords.confirmPassword}
                onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
              />
              <Button
                variant="outline-secondary"
                onClick={() => togglePasswordVisibility('confirm')}
              >
                {showPasswordFields.confirm ? 'Hide' : 'Show'}
              </Button>
            </InputGroup>
          </Form.Group>
          {passwordMessage && <div className="text-danger mt-2">{passwordMessage}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            resetPasswordState();
            setAccountSuccess('');
          }}>Cancel</Button>
          <Button variant="primary" onClick={handlePasswordChange}>Save</Button>
        </Modal.Footer>
      </Modal>

      {/* User ID Modal */}
      <Modal
        show={showUserIdModal}
        onHide={() => {
          setShowUserIdModal(false);
          setUserIdMessage('');
          setNewUserId(state.user?.userId || state.user?.user_id || '');
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Change User ID</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>New User ID</Form.Label>
            <Form.Control
              type="text"
              value={newUserId}
              placeholder="Enter new user ID"
              onChange={(e) => setNewUserId(e.target.value)}
            />
          </Form.Group>
          {userIdMessage && <div className="text-danger">{userIdMessage}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowUserIdModal(false);
              setUserIdMessage('');
              setNewUserId(state.user?.userId || state.user?.user_id || '');
            }}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUserIdChange}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showBotKeyModal}
        onHide={resetBotKeyModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Create Bot API Key</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Label</Form.Label>
            <Form.Control
              type="text"
              value={newBotKey.label}
              placeholder="OpenAI assistant"
              onChange={(e) => setNewBotKey((prev) => ({ ...prev, label: e.target.value }))}
            />
            <Form.Text className="text-muted">
              Use a label that identifies which bot or integration will receive this key.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Expires At</Form.Label>
            <Form.Control
              type="datetime-local"
              value={newBotKey.expires_at}
              onChange={(e) => setNewBotKey((prev) => ({ ...prev, expires_at: e.target.value }))}
            />
            <Form.Text className="text-muted">
              Optional. Leave blank for a key with no expiration date.
            </Form.Text>
          </Form.Group>

          {createdBotToken && (
            <Alert variant="warning">
              <div className="fw-semibold mb-2">Copy this API key now</div>
              <div className="small mb-2">
                Store it somewhere secure before closing this window. You will not be able to view the full key again.
              </div>
              <Form.Control
                type="text"
                readOnly
                value={createdBotToken}
                onFocus={(e) => e.target.select()}
              />
              <div className="small mt-2">
                After this step, only the key label and metadata will remain visible in your account.
              </div>
            </Alert>
          )}

          {botKeyError && <Alert variant="danger" className="mb-0">{botKeyError}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={resetBotKeyModal}>
            Close
          </Button>
          <Button variant="primary" onClick={handleCreateBotKey} disabled={creatingBotKey}>
            {creatingBotKey ? 'Creating...' : 'Create Key'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showTeamModal} onHide={() => {
        setShowTeamModal(false);
        setSelectedTeam(null);
        setTeamError('');
        setQuery('');
      }} size="lg">
  <Modal.Header closeButton>
    <Modal.Title>{realtorView ? 'Create or Update Team' : 'Create or Update Business'}</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    {realtorView && (
      <p className="mb-3">
        Can’t find your office?{" "}
        <a
          href="https://directories.apps.realtor/?type=office"
          target="_blank"
          rel="noopener noreferrer"
        >
          Search NAR Directory
        </a>
      </p>
    )}

    {/* Search Input */}
    {realtorView && (
      <Form.Group className="mb-3">
        <Form.Label>Search Brokerage</Form.Label>
        <Form.Control
          type="text"
          value={query}
          placeholder="Start typing to search brokerages"
          onChange={(e) => setQuery(e.target.value)}
        />
      </Form.Group>
    )}

    {realtorView && pendingDb && <Spinner animation="border" size="sm" className="mb-3" />}
    {teamError && <div className="text-danger mb-3">{teamError}</div>}

    {realtorView && dbResults.length > 0 && (
      <ListGroup className="mb-4">
        {dbResults.map((b) => (
          <ListGroup.Item
            key={b.id}
            action
            onClick={() =>
              setSelectedTeam({
                ...buildSelectedTeam(),
                name: b.office_business_name,
                city: b.office_city,
                state: b.office_state,
                phone: b.phone_number,
                team_type: 'brokerage',
              })
            }
            active={selectedTeam?.name === b.office_business_name && selectedTeam?.city === b.office_city}
          >
            <div className="fw-bold">{b.office_business_name}</div>
            <small>
              {b.office_city}, {b.office_state} — {b.broker}
            </small>
          </ListGroup.Item>
        ))}
      </ListGroup>
    )}

    {/* Manual Edit Fields */}
    <Form>
      <Form.Group className="mb-2">
        <Form.Label>{realtorView ? 'Team Name' : 'Business Name'}</Form.Label>
        <Form.Control
          type="text"
          value={selectedTeam?.name || ''}
          onChange={(e) =>
            setSelectedTeam({
              ...selectedTeam,
              name: e.target.value
            })
          }
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>Type</Form.Label>
        <Form.Select
          value={selectedTeam?.team_type || (realtorView ? 'brokerage' : 'business')}
          onChange={(e) =>
            setSelectedTeam({
              ...selectedTeam,
              team_type: e.target.value
            })
          }
        >
          <option value="business">Business</option>
          <option value="team">Team</option>
          <option value="brokerage">Brokerage</option>
          <option value="department">Department</option>
          <option value="other">Other</option>
        </Form.Select>
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>City</Form.Label>
        <Form.Control
          type="text"
          value={selectedTeam?.city || ''}
          onChange={(e) =>
            setSelectedTeam({
              ...selectedTeam,
              city: e.target.value
            })
          }
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>State</Form.Label>
        <Form.Control
          type="text"
          value={selectedTeam?.state || ''}
          onChange={(e) =>
            setSelectedTeam({
              ...selectedTeam,
              state: e.target.value
            })
          }
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>Street Address</Form.Label>
        <Form.Control
          type="text"
          value={selectedTeam?.street_address || ''}
          onChange={(e) =>
            setSelectedTeam({
              ...selectedTeam,
              street_address: e.target.value
            })
          }
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>Postal Code</Form.Label>
        <Form.Control
          type="text"
          value={selectedTeam?.postal_code || ''}
          onChange={(e) =>
            setSelectedTeam({
              ...selectedTeam,
              postal_code: e.target.value
            })
          }
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>Phone</Form.Label>
        <Form.Control
          type="text"
          value={selectedTeam?.phone || ''}
          onChange={(e) =>
            setSelectedTeam({
              ...selectedTeam,
              phone: e.target.value
            })
          }
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>Email</Form.Label>
        <Form.Control
          type="email"
          value={selectedTeam?.email || ''}
          onChange={(e) =>
            setSelectedTeam({
              ...selectedTeam,
              email: e.target.value
            })
          }
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>Website</Form.Label>
        <Form.Control
          type="text"
          value={selectedTeam?.website || ''}
          onChange={(e) =>
            setSelectedTeam({
              ...selectedTeam,
              website: e.target.value
            })
          }
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>Description</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={selectedTeam?.description || ''}
          onChange={(e) =>
            setSelectedTeam({
              ...selectedTeam,
              description: e.target.value
            })
          }
        />
      </Form.Group>
    </Form>
  </Modal.Body>
  <Modal.Footer>
    <Button variant="secondary" onClick={() => {
      setShowTeamModal(false);
      setSelectedTeam(null);
      setTeamError('');
      setQuery('');
    }}>
      Cancel
    </Button>
    <Button
      variant="primary"
      disabled={!selectedTeam?.name || !selectedTeam?.city || !selectedTeam?.state}
      onClick={confirmSelectedTeam}
    >
      Save Team
    </Button>
  </Modal.Footer>
</Modal>

      <Modal show={showJoinTeamModal} onHide={resetJoinRequestModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Request to Join a Team</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Search teams or businesses</Form.Label>
            <Form.Control
              type="text"
              value={joinQuery}
              placeholder="Search by team name, city, or state"
              onChange={(e) => setJoinQuery(e.target.value)}
            />
          </Form.Group>

          {pendingTeamSearch && <Spinner animation="border" size="sm" className="mb-3" />}
          {joinRequestError && <Alert variant="danger" className="mb-3">{joinRequestError}</Alert>}

          {teamSearchResults.length > 0 && (
            <ListGroup className="mb-3">
              {teamSearchResults.map((teamResult) => (
                <ListGroup.Item
                  key={teamResult.id}
                  action
                  active={selectedJoinTeam?.id === teamResult.id}
                  onClick={() => setSelectedJoinTeam(teamResult)}
                >
                  <div className="fw-semibold">{teamResult.name}</div>
                  <div className="small text-muted">
                    {[teamResult.city, teamResult.state].filter(Boolean).join(', ')}
                    {teamResult.team_type ? ` • ${teamResult.team_type}` : ''}
                  </div>
                  {teamResult.street_address && (
                    <div className="small text-muted">{teamResult.street_address}</div>
                  )}
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}

          {joinQuery.trim().length >= 2 && !pendingTeamSearch && teamSearchResults.length === 0 && (
            <div className="text-muted small mb-3">No teams matched that search.</div>
          )}

          {selectedJoinTeam && (
            <Card className="border bg-light-subtle mb-3">
              <Card.Body>
                <div className="fw-semibold">{selectedJoinTeam.name}</div>
                <div className="small text-muted">
                  {[selectedJoinTeam.city, selectedJoinTeam.state].filter(Boolean).join(', ')}
                </div>
              </Card.Body>
            </Card>
          )}

          <Form.Group>
            <Form.Label>Message for the owner or admin</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={joinMessage}
              placeholder="Optional note about why you want to join this team"
              onChange={(e) => setJoinMessage(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={resetJoinRequestModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!selectedJoinTeam?.id || submittingJoinRequest}
            onClick={handleJoinRequest}
          >
            {submittingJoinRequest ? 'Submitting...' : 'Send Request'}
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
}

export default Account;
